import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { trpc } from "@/lib/trpc";
import { useQuadro } from "@/hooks/useQuadro";
import { ETAPA_COR } from "@shared/etapas";

// Hook helper: clientId ativo (respeitando o tenant switcher do raz admin).
// Antes a query Reportei não passava clientId, então o server caía pro JWT do
// usuário — pra raz admin isso significava "sempre o tenant do admin", não o
// que ele selecionou no switcher.
function useActiveClientId(): number | undefined {
  const { session } = useAuth();
  return session?.activeClientId ?? session?.clientId ?? undefined;
}

// ─── Reportei: inteligência de conteúdo orgânico (IG + LinkedIn) ──────────────

function fmtNum(n: number | null | undefined): string {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".", ",") + "mi";
  if (abs >= 1_000) return (n / 1_000).toFixed(1).replace(".", ",") + "mil";
  return String(Math.round(n));
}

function ReporteiPanel() {
  const [rede, setRede] = useState<"instagram" | "linkedin">("instagram");
  const [dias, setDias] = useState(30);
  const clientId = useActiveClientId();
  // clientId entra no input → vira parte do query key automaticamente, então
  // trocar de tenant no switcher invalida o cache e re-fetcha.
  const { data, isLoading, error } = trpc.reportei.dashboard.useQuery(
    { rede, dias, clientId },
    { retry: false, refetchOnWindowFocus: false, enabled: clientId != null }
  );

  const kpis = !data ? [] : rede === "instagram"
    ? [
        { label: "Seguidores", value: fmtNum(data.currentFollowers) },
        { label: "Novos seguidores", value: fmtNum(data.followersTotal) },
        { label: "Alcance total", value: fmtNum(data.reachTotal) },
        { label: "Alcance orgânico", value: fmtNum(data.organicReach) },
        { label: "Alcance pago", value: fmtNum(data.paidReach) },
        { label: "Posts no período", value: fmtNum(data.posts.length) },
      ]
    : [
        { label: "Seguidores", value: fmtNum(data.liFollowersTotal) },
        { label: "Novos seguidores", value: fmtNum(data.liNewFollowers) },
        { label: "Impressões", value: fmtNum(data.impressionsTotal) },
        { label: "Engajamento", value: fmtNum(data.engagementTotal) },
        { label: "Taxa de engaj.", value: data.engagementRate != null ? data.engagementRate.toFixed(1).replace(".", ",") + "%" : "—" },
        { label: "Cliques", value: fmtNum(data.totalClicks) },
      ];

  const topPosts = data
    ? [...data.posts].sort((a, b) => b.performanceScore - a.performanceScore).slice(0, 5)
    : [];

  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-bold text-sm text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
            Desempenho orgânico — via Reportei
          </h3>
          <p className="text-[var(--solar-mute)] text-xs mt-0.5">Instagram e LinkedIn · dados reais de redes sociais</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-[var(--solar-bg)] rounded-lg p-1">
            {(["instagram", "linkedin"] as const).map(r => (
              <button key={r} onClick={() => setRede(r)}
                className="px-3 py-1.5 rounded-md text-xs font-semibold transition-colors"
                style={{
                  background: rede === r ? "white" : "transparent",
                  color: rede === r ? "var(--solar-ink)" : "var(--solar-mute)",
                  boxShadow: rede === r ? "var(--elev-1)" : "none",
                }}>
                {r === "instagram" ? "Instagram" : "LinkedIn"}
              </button>
            ))}
          </div>
          <select value={dias} onChange={e => setDias(Number(e.target.value))}
            className="text-xs border border-[var(--solar-line)] rounded-lg px-2 py-1.5 bg-white text-[var(--solar-ink)] focus:outline-none focus:ring-1 focus:ring-[var(--brand)]">
            <option value={30}>30 dias</option>
            <option value={60}>60 dias</option>
            <option value={90}>90 dias</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-28">
          <div className="w-6 h-6 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--solar-ink)] animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700">
          Não foi possível carregar os dados do Reportei: {error.message}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {kpis.map(k => (
              <div key={k.label} className="rounded-lg p-3" style={{ background: "var(--solar-bg)" }}>
                <p className="text-[10px] font-semibold text-[var(--solar-mute)] uppercase tracking-wider leading-tight">{k.label}</p>
                <p className="text-xl font-bold text-[var(--solar-ink)] mt-1" style={{ fontFamily: "Poppins, sans-serif" }}>{k.value}</p>
              </div>
            ))}
          </div>

          {topPosts.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] font-bold text-[var(--solar-ink)] uppercase tracking-widest mb-2">Top posts do período</p>
              <div className="space-y-1.5">
                {topPosts.map(p => (
                  <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--solar-bg)] transition-colors">
                    <span className="text-xs font-bold w-9 text-center shrink-0 px-1.5 py-0.5 rounded"
                      style={{ background: p.performanceScore >= 70 ? "#D1FAE5" : "var(--solar-bg)", color: p.performanceScore >= 70 ? "#10B981" : "var(--solar-mute)" }}>
                      {p.performanceScore}
                    </span>
                    <span className="flex-1 min-w-0 text-xs text-[var(--ink-2)] truncate">{p.text || "(sem texto)"}</span>
                    <span className="text-[11px] text-[var(--solar-mute)] shrink-0">{p.type}</span>
                    <span className="text-[11px] font-semibold text-[var(--solar-ink)] shrink-0 w-16 text-right">
                      {fmtNum(p.reach || p.impressions || 0)} alc.
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}
          {data && (
            <p className="text-[10px] text-[var(--solar-mute)] mt-3">
              Atualizado em {new Date(data.fetchedAt).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

const REDE_ICON: Record<string, string> = { instagram: "", tiktok: "", youtube: "▶️", linkedin: "", twitter: "𝕏" };
const STATUS_LABEL: Record<string, { label: string; color: string; bg: string }> = {
  rascunho: { label: "Rascunho", color: "#52525B", bg: "#FEF3C7" },
  revisao:  { label: "Revisão",  color: "#EC4899", bg: "#FCE7F3" },
  aprovado: { label: "Aprovado", color: "#10B981", bg: "#D1FAE5" },
};

// ─── Mini componentes ─────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color, bg }: { label: string; value: number | string; sub?: string; color: string; bg: string }) {
  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-4 flex flex-col gap-1">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-1" style={{ background: bg }}>
        <div className="w-3 h-3 rounded-full" style={{ background: color }} />
      </div>
      <p className="text-2xl num-hero">{value}</p>
      <p className="text-xs font-semibold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{label}</p>
      {sub && <p className="text-[10px] text-[var(--solar-mute)]">{sub}</p>}
    </div>
  );
}

function BarChart({ data, maxValue, colorFn }: {
  data: { label: string; value: number; color?: string; bg?: string }[];
  maxValue?: number;
  colorFn?: (label: string) => { color: string; bg: string };
}) {
  const max = maxValue ?? Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-2">
      {data.map(d => {
        const info = colorFn ? colorFn(d.label) : { color: d.color ?? "var(--solar-ink)", bg: d.bg ?? "#EDEBE6" };
        const pct = Math.round((d.value / max) * 100);
        return (
          <div key={d.label} className="flex items-center gap-2">
            <p className="text-xs text-[var(--ink-2)] w-20 shrink-0 truncate" style={{ fontFamily: "Poppins, sans-serif" }}>{d.label}</p>
            <div className="flex-1 h-5 bg-[var(--solar-bg)] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                style={{ width: `${Math.max(pct, 4)}%`, background: `linear-gradient(90deg, ${info.bg}, ${info.color})` }}
              />
            </div>
            <span className="text-xs font-bold text-[var(--solar-ink)] w-6 text-right shrink-0" style={{ fontFamily: "Poppins, sans-serif" }}>{d.value}</span>
          </div>
        );
      })}
    </div>
  );
}

function MiniLineChart({ data }: { data: { mes: string; pautas: number; textos: number }[] }) {
  if (data.length < 2) return null;
  const maxPautas = Math.max(...data.map(d => d.pautas), 1);
  const maxTextos = Math.max(...data.map(d => d.textos), 1);
  const w = 300, h = 80, pad = 10;
  const xStep = (w - pad * 2) / (data.length - 1);

  function points(values: number[], maxVal: number) {
    return values.map((v, i) => {
      const x = pad + i * xStep;
      const y = pad + (h - pad * 2) * (1 - v / maxVal);
      return `${x},${y}`;
    }).join(" ");
  }

  const pautasPts = points(data.map(d => d.pautas), maxPautas);
  const textosPts = points(data.map(d => d.textos), maxTextos);

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: 80 }}>
        <polyline points={pautasPts} fill="none" stroke="var(--solar-ink)" strokeWidth="2" strokeLinejoin="round" />
        <polyline points={textosPts} fill="none" stroke="#71717A" strokeWidth="2" strokeLinejoin="round" />
        {data.map((d, i) => {
          const x = pad + i * xStep;
          const yP = pad + (h - pad * 2) * (1 - d.pautas / maxPautas);
          const yT = pad + (h - pad * 2) * (1 - d.textos / maxTextos);
          return (
            <g key={i}>
              <circle cx={x} cy={yP} r="3" fill="var(--solar-ink)" />
              <circle cx={x} cy={yT} r="3" fill="#71717A" />
            </g>
          );
        })}
      </svg>
      <div className="flex justify-between mt-1">
        {data.map(d => (
          <span key={d.mes} className="text-[9px] text-[var(--solar-mute)]" style={{ fontFamily: "Poppins, sans-serif" }}>
            {d.mes.substring(5)}
          </span>
        ))}
      </div>
      <div className="flex gap-4 mt-2">
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-[var(--solar-ink)] rounded-full" />
          <span className="text-[10px] text-[var(--solar-mute)]" style={{ fontFamily: "Poppins, sans-serif" }}>Pautas</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-0.5 bg-[#71717A] rounded-full" />
          <span className="text-[10px] text-[var(--solar-mute)]" style={{ fontFamily: "Poppins, sans-serif" }}>Textos</span>
        </div>
      </div>
    </div>
  );
}

// ─── Página Métricas ──────────────────────────────────────────────────────────

// ─── HU-076: comparativo entre períodos ──────────────────────────────────────

function DeltaItem({ label, atual, anterior, color }: { label: string; atual: number; anterior: number; color: string }) {
  const diff = atual - anterior;
  const pct = anterior > 0 ? Math.round((diff / anterior) * 100) : (atual > 0 ? 100 : 0);
  const up = diff > 0, flat = diff === 0;
  const cor = flat ? "#9CA3AF" : up ? "#10B981" : "#EF4444";
  return (
    <div className="flex-1 min-w-[140px]">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color }}>{label}</p>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl num-hero">{atual}</span>
        <span className="text-xs font-semibold flex items-center gap-0.5" style={{ color: cor }}>
          {flat ? "—" : up ? "▲" : "▼"} {flat ? "" : `${Math.abs(pct)}%`}
        </span>
      </div>
      <p className="text-[11px] text-[var(--solar-mute)] mt-0.5">
        mês anterior: {anterior} · {flat ? "estável" : up ? `+${diff}` : diff}
      </p>
    </div>
  );
}

function ComparativoPeriodos({ meses }: { meses: { mes: string; pautas: number; textos: number }[] }) {
  if (meses.length < 2) return null;
  const atual = meses[meses.length - 1];
  const anterior = meses[meses.length - 2];
  const fmt = (m: string) => {
    const [y, mo] = m.split("-");
    return new Date(Number(y), Number(mo) - 1).toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  };
  return (
    <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>
          Comparativo de períodos
        </h3>
        <span className="text-xs text-[var(--solar-mute)] capitalize">{fmt(atual.mes)} vs {fmt(anterior.mes)}</span>
      </div>
      <div className="flex flex-wrap gap-6">
        <DeltaItem label="Pautas criadas" atual={atual.pautas} anterior={anterior.pautas} color="var(--solar-ink)" />
        <DeltaItem label="Textos criados" atual={atual.textos} anterior={anterior.textos} color="#71717A" />
      </div>
    </div>
  );
}

// Etapa: rótulo do quadro do cliente e cor global (out/26; era lista fixa com Roteiro).
function useInfoEtapa() {
  const { rotulo, noQuadro, etapasPauta } = useQuadro();
  const info = (id: string) => {
    const color = noQuadro(id) ? (ETAPA_COR[id] ?? "var(--solar-mute)") : "#B42318";
    return { label: noQuadro(id) ? rotulo(id) : `${rotulo(id)} (fora do quadro)`, color, bg: color.startsWith("#") ? color + "1A" : "var(--solar-bg)" };
  };
  return { info, etapasPauta };
}

export default function Metricas() {
  const { session } = useAuth();
  const clientId = session?.activeClientId ?? session?.clientId ?? 1;
  const { info: infoEtapa, etapasPauta } = useInfoEtapa();

  const { data, isLoading } = trpc.metricas.summary.useQuery(
    { clientId }, { enabled: !!clientId }
  );

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-[var(--solar-bg)]">
        <div className="w-10 h-10 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--solar-ink)] animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const pautasPorColuna = Object.entries(data.pautas.porColuna).map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
  const pautasPorRede = Object.entries(data.pautas.porRede).map(([label, value]) => ({ label: `${REDE_ICON[label] ?? ""} ${label}`, value }))
    .sort((a, b) => b.value - a.value);
  const textosPorStatus = Object.entries(data.textos.porStatus).map(([label, value]) => ({ label, value }));
  const pecasPorTipo = Object.entries(data.pecas.porTipo).map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

  const taxaPublicacao = data.pautas.total > 0
    ? Math.round(((data.pautas.porColuna["publicado"] ?? 0) / data.pautas.total) * 100)
    : 0;
  const taxaAprovacao = data.textos.total > 0
    ? Math.round(((data.textos.porStatus["aprovado"] ?? 0) / data.textos.total) * 100)
    : 0;

  return (
    <div className="h-full overflow-y-auto bg-[var(--solar-bg)]">
      {/* Header */}
      <div className="bg-white border-b border-[var(--solar-line)] px-6 py-4 sticky top-0 z-10">
        <h1 className="font-bold text-lg text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>Métricas</h1>
        <p className="text-[var(--solar-mute)] text-xs mt-0.5">Visão geral do conteúdo produzido</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Desempenho orgânico real — Reportei */}
        <ReporteiPanel />

        {/* Cards de resumo */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total de pautas" value={data.pautas.total} sub={`${taxaPublicacao}% publicadas`} color="var(--solar-ink)" bg="#EDEBE6" />
          <StatCard label="Textos" value={data.textos.total} sub={`${taxaAprovacao}% aprovados`} color="#71717A" bg="#FDE8F5" />
          <StatCard label="Peças na biblioteca" value={data.pecas.total} color="#10B981" bg="#D1FAE5" />
          <StatCard label="Publicadas" value={data.pautas.porColuna["publicado"] ?? 0} sub="pautas no ar" color="#262626" bg="#EDEBE6" />
        </div>

        {/* HU-076: comparativo entre períodos */}
        <ComparativoPeriodos meses={data.ultimosMeses} />

        {/* Taxa de publicação */}
        <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
          <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
            Pipeline de conteúdo
          </h3>
          <div className="flex items-end gap-1 h-20">
            {[...etapasPauta.filter(c => c.id !== "desaprovado").map(c => c.id),
              ...Object.keys(data.pautas.porColuna).filter(id => !etapasPauta.some(c => c.id === id))].map(col => {
              const info = infoEtapa(col);
              const count = data.pautas.porColuna[col] ?? 0;
              const max = Math.max(...Object.values(data.pautas.porColuna), 1);
              const pct = Math.round((count / max) * 100);
              return (
                <div key={col} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-xs font-bold text-[var(--solar-ink)]" style={{ fontFamily: "Poppins, sans-serif" }}>{count}</span>
                  <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max(pct * 0.5 + 4, 8)}px`, background: info.color, opacity: 0.85 }} />
                  <span className="text-[9px] text-[var(--solar-mute)] text-center leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>{info.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Grid de gráficos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pautas por rede */}
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
            <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Pautas por rede social
            </h3>
            <BarChart
              data={pautasPorRede}
              colorFn={() => ({ color: "var(--solar-ink)", bg: "#EDEBE6" })}
            />
          </div>

          {/* Textos por status */}
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
            <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Textos por status
            </h3>
            <BarChart
              data={textosPorStatus}
              colorFn={label => STATUS_LABEL[label] ?? { color: "var(--solar-ink)", bg: "#EDEBE6" }}
            />
            <div className="mt-4 pt-4 border-t border-[var(--solar-line)]">
              <div className="flex justify-between text-xs text-[var(--solar-mute)]">
                <span style={{ fontFamily: "Poppins, sans-serif" }}>Taxa de aprovação</span>
                <span className="font-bold text-[#10B981]">{taxaAprovacao}%</span>
              </div>
              <div className="mt-1.5 h-2 bg-[var(--solar-bg)] rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-[#10B981] transition-all duration-700" style={{ width: `${taxaAprovacao}%` }} />
              </div>
            </div>
          </div>

          {/* Peças por tipo */}
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
            <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Peças por tipo
            </h3>
            <BarChart
              data={pecasPorTipo}
              colorFn={() => ({ color: "#262626", bg: "#EDEBE6" })}
            />
          </div>

          {/* Evolução mensal */}
          <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
            <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
              Evolução nos últimos 6 meses
            </h3>
            {data.ultimosMeses.length >= 2 ? (
              <MiniLineChart data={data.ultimosMeses} />
            ) : (
              <div className="flex items-center justify-center h-20 text-[var(--solar-mute)] text-sm">
                Dados insuficientes
              </div>
            )}
          </div>
        </div>

        {/* Pautas por coluna detalhado */}
        <div className="bg-white rounded-xl border border-[var(--solar-line)] shadow-[var(--elev-1)] p-5">
          <h3 className="font-bold text-sm text-[var(--solar-ink)] mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>
            Distribuição do kanban
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {pautasPorColuna.map(({ label, value }) => {
              const info = infoEtapa(label);
              const pct = data.pautas.total > 0 ? Math.round((value / data.pautas.total) * 100) : 0;
              return (
                <div key={label} className="rounded-xl p-3 flex flex-col gap-1" style={{ background: info.bg }}>
                  <p className="text-xl font-bold" style={{ color: info.color, fontFamily: "Poppins, sans-serif" }}>{value}</p>
                  <p className="text-xs font-semibold" style={{ color: info.color, fontFamily: "Poppins, sans-serif" }}>{info.label}</p>
                  <p className="text-[10px]" style={{ color: info.color + "99" }}>{pct}% do total</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
